import { useCallback, useState } from 'react';
import { InfoDialog } from '../../common/dialogs/InfoDialog';
import { Box, Stack } from '@mui/material';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import { colors, Typography } from '@sweep-io/sweep-design';
import { DataTable } from '../../common/table/DataTable';
import { AutomationVersionHistoryFilters } from './AutomationVersionHistoryFilters';
import isEqual from 'lodash/isEqual';
import { DeployStatusForTable } from '../../../types/enums/DeployStatusForTable';
import { useAutomationsContext } from '../AutomationsContext';
import { SortOrder } from '../../common/types';
import {
  AutomationVersionHistoryTableFilters,
  ActiveStatus,
  DataTableRow,
  AutomationVersionHistoryFilterOptions,
} from './types';
import { AutomationType } from '../../../types/enums/AutomationType';
import { useSelector } from 'react-redux';
import { selectAccountUsers } from '../../../reducers/accountUsersReducer';
import groupBy from 'lodash/groupBy';
import { useInitializeVersionHistory } from './useInitializeVersionHistory';
import { getRows, getTableColumns } from './utils';
import { ReadOnlySection } from './ReadOnlySection';
import { DataTableId } from '../../common/table/utils';

export const AutomationVersionHistoryDialog = ({
  automation,
  closeDialog,
}: {
  automation: AutomationStructureNew;
  closeDialog: () => void;
}) => {
  const [sortAndFilter, setSortAndFilter] = useState<AutomationVersionHistoryTableFilters>({});
  const [activeRow, setActiveRow] = useState<string>(automation.versionId);
  const [sortState, setSortState] = useState<{ sortBy: string; sortOrder: SortOrder }>({
    sortBy: 'deployedAt',
    sortOrder: SortOrder.DESC,
  });

  const { setAutomationJson, automationType } = useAutomationsContext();

  const users = useSelector(selectAccountUsers);
  const usersById = groupBy(users, 'id');

  const {
    isLoading,
    historyDataList,
    originalVersionList,
    versionToShowInfoOn,
    setHistoryDataList,
  } = useInitializeVersionHistory(automation.automationId);

  const currentVersion = historyDataList?.find((item) => item.versionId === activeRow);
  const isDedupeOrMatching =
    automationType === AutomationType.Dedupe || automationType === AutomationType.Matching;

  const tableColumns = getTableColumns(historyDataList);
  const rows = getRows({ historyDataList, activeRow, usersById, versionToShowInfoOn });

  const filterOptions: AutomationVersionHistoryFilterOptions = {
    status: [ActiveStatus.ACTIVE, ActiveStatus.INACTIVE],
    deployedByName: [
      ...new Set(originalVersionList.map((item) => item.deployedByName).filter(Boolean)),
    ] as string[],
  };

  const handleFilterChange = useCallback(
    async (newFilter: Partial<AutomationVersionHistoryTableFilters>) => {
      if (!isEqual(newFilter, sortAndFilter)) {
        const newObj = { ...sortAndFilter, ...newFilter };
        setSortAndFilter(newObj);
        // Apply filters to the data
        const newData = originalVersionList.filter((item) => {
          if (newObj.status?.length) {
            const isActive = item.isActive;
            const statusMatch = newObj.status.includes(
              isActive ? ActiveStatus.ACTIVE : ActiveStatus.INACTIVE,
            );
            if (!statusMatch) return false;
          }

          if (newObj.deployedByName?.length && item.deployedByName) {
            if (!newObj.deployedByName.includes(item.deployedByName)) return false;
          }

          if (newObj.deployedAtStartTime && newObj.deployedAtEndTime) {
            const deployedAt = new Date(item?.deployedAt ?? '');
            const startTime = new Date(newObj.deployedAtStartTime);
            const endTime = new Date(newObj.deployedAtEndTime);
            if (deployedAt < startTime || deployedAt > endTime) return false;
          }

          return true;
        });

        setHistoryDataList(newData);

        // Handle activeRow selection after filtering
        if (newData.length === 0) {
          setActiveRow('');
        } else if (!newData.some((item) => item.versionId === activeRow)) {
          setActiveRow(newData[0].versionId);
        }
      }
    },
    [sortAndFilter, activeRow, originalVersionList, setHistoryDataList],
  );

  const onRowClick = useCallback(
    (_: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      setActiveRow(row.id);
    },
    [],
  );

  const handleSetAsDraft = useCallback(() => {
    let currentAutomation = historyDataList?.find(
      (item) => item.versionId === activeRow,
    ) as AutomationStructureNew;
    if (currentAutomation?.status === DeployStatusForTable.Deployed) {
      currentAutomation = {
        ...automation,
        automationDetails: currentAutomation?.automationDetails as AutomationDetailsDtoNew,
        funnelRecordType: currentAutomation?.funnelRecordType,
      };
    }
    if (currentAutomation) {
      setAutomationJson(currentAutomation);
      closeDialog();
    }
  }, [historyDataList, activeRow, closeDialog, setAutomationJson, automation]);

  const handleSortChange = useCallback((newSortState: { sortBy: string; sortOrder: SortOrder }) => {
    setSortState(newSortState);
  }, []);

  return (
    <InfoDialog
      handleClose={(e) => {
        e.stopPropagation();
        closeDialog();
      }}
      open={true}
      PaperPropsSx={{ width: '1480px', height: '100%' }}
      showCloseButton
      titleJsx={`'${automation.name}' - Version history`}
      titleTypographyVariant="h3"
      dialogContentSx={{ borderTop: `1px solid ${colors.grey[200]}`, p: 0 }}
    >
      {isLoading && (
        <Box m={2}>
          <CenteredCircularProgress />
        </Box>
      )}
      {!isLoading && (
        <Stack direction="row" justifyContent="space-between" height="100%">
          <Box
            sx={{
              p: '16px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              maxWidth: 'calc(100vw - 530px)', //530 - width of the side panel and spacing
            }}
          >
            <AutomationVersionHistoryFilters
              setFilter={handleFilterChange}
              sortAndFilter={sortAndFilter}
              disableFilters={false}
              filterOptions={filterOptions}
            />
            <Box sx={{ maxHeight: '750px', overflow: 'hidden', width: '100%' }}>
              <DataTable
                tableId={DataTableId.AutomationVersionHistoryDialog}
                hideChevronButton
                stickyHeader={true}
                columns={tableColumns}
                rows={rows}
                isLoading={isLoading}
                onRowClick={onRowClick}
                defaultSortState={sortState}
                onSortChange={handleSortChange}
                tableEmptyStateJsx={
                  <Box sx={{ mt: 3, textAlign: 'center', mb: 2 }}>
                    <Typography variant="caption" color={colors.grey[800]}>
                      No versions found
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>

          <ReadOnlySection
            activeRow={activeRow}
            originalVersionList={originalVersionList}
            currentVersion={currentVersion}
            isDedupeOrMatching={isDedupeOrMatching}
            automationType={automationType}
            handleSetAsDraft={handleSetAsDraft}
          />
        </Stack>
      )}
    </InfoDialog>
  );
};
